import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { MessageCircle, Trash2, Workflow, ExternalLink, X } from 'lucide-react';
import { Badge, Button, Input, Card, Alert, Loader, Select, Tabs } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import ModelSelect from '@/components/ModelSelect';
import { groupConnectionOptions } from '@/lib/connectionOptions';
import { useChatDock } from '@/lib/ChatDockContext';
import Can from '@rbac/Can';
import ToolProviderCreateDialog from '@/components/ToolProviderCreateDialog';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import AgentA2aTab, { skillsFromJson, skillsToJson } from '@/components/AgentA2aTab';

function fieldsChanged(a, b) {
  return JSON.stringify(a) !== JSON.stringify(b);
}

function parseJsonArray(value) {
  try {
    const v = JSON.parse(value || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function enforceOnePerCategory(nextIds, prevIds, connections) {
  const byId = new Map(connections.map((c) => [Number(c.id), c]));
  const added = nextIds.filter((id) => !prevIds.includes(id));
  let out = [...nextIds];
  for (const id of added) {
    const conn = byId.get(Number(id));
    if (!conn) continue;
    out = out.filter((other) => Number(other) === Number(id)
      || !byId.get(Number(other)) || byId.get(Number(other)).category_key !== conn.category_key);
  }
  return out;
}

function SectionCard({ title, count, children, help }) {
  return (
    <Card className="p-3 space-y-2">
      <div className="text-sm font-semibold">{title}{count != null ? ` — ${count}` : ''}</div>
      {children}
      {help && <p className="text-xs text-secondary-500">{help}</p>}
    </Card>
  );
}

function Notice({ children }) {
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-sm px-3 py-2">
      {children}
    </div>
  );
}

function shouldShowApiKeyControl(form, aiConnections, agentId) {
  return form.auth_mode !== 'subscription' && aiConnections.length === 0 && !!agentId;
}

function ApiKeyControl({ agentId, providerLabel }) {
  const { fetchApi } = useAppApi();
  const [hasOverride, setHasOverride] = useState(false);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchApi('agentApiKeyStatus', agentId).then((d) => setHasOverride(d.hasOverride)).catch(() => {});
  }, [agentId]);

  const submit = async () => {
    setBusy(true);
    setStatus('');
    try {
      const d = await fetchApi('agentSetApiKey', { agentId, apiKey: input });
      setHasOverride(d.hasOverride);
      setInput('');
      setStatus(d.hasOverride ? 'Saved.' : 'Cleared — using this app\'s shared key.');
    } catch (ex) {
      setStatus((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="block text-sm mb-1">API key override for {providerLabel} (optional)</label>
      <div className="flex gap-2">
        <input
          type="password"
          autoComplete="off"
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] flex-1"
          placeholder={hasOverride ? 'Already set — enter a new value to replace, or clear' : "Leave blank to use this app's .env key"}
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <Button size="sm" variant="secondary" onClick={submit} disabled={busy}>{busy ? '…' : 'Set'}</Button>
      </div>
      <p className="text-xs text-secondary-500 mt-1">
        {hasOverride ? 'This agent uses its own key for this provider, not the shared .env key.' : "Using this app's shared .env key for this provider."}
      </p>
      {status && <p className="text-xs text-emerald-600 mt-1">{status}</p>}
    </div>
  );
}

function ProviderOrConnectionField({ form, setField, providers, aiConnections }) {
  const providerLabelOf = (key) => providers.find((p) => p.key === key)?.label || key;

  if (!aiConnections.length) {
    return (
      <div>
        <label className="block text-sm mb-1">Provider</label>
        <select
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
          value={form.provider || 'openai'}
          onChange={(e) => { setField('provider', e.target.value); setField('provider_connection_id', null); }}
        >
          {providers.map((p) => (
            <option key={p.key} value={p.key}>{p.label}{p.configured ? '' : ' — no API key set'}</option>
          ))}
        </select>
      </div>
    );
  }

  const value = form.provider_connection_id != null && form.provider_connection_id !== ''
    ? String(form.provider_connection_id) : '';
  const pick = (e) => {
    const conn = aiConnections.find((c) => String(c.id) === e.target.value);
    setField('provider_connection_id', conn ? conn.id : null);
    if (conn) setField('provider', conn.provider);
  };
  return (
    <div>
      <label className="block text-sm mb-1">AI Model connection</label>
      <select
        className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
        value={value}
        onChange={pick}
      >
        <option value="">Default — auto key for {providerLabelOf(form.provider || 'openai')}</option>
        {aiConnections.map((c) => (
          <option key={c.id} value={c.id}>{c.name} ({providerLabelOf(c.provider)}){c.has_credentials ? '' : ' — no credentials'}</option>
        ))}
      </select>
      <p className="text-xs text-secondary-500 mt-1">Its provider is used automatically — pick the exact key to run this agent.</p>
    </div>
  );
}

function ModelSection({ form, setField, providers, aiConnections, agentId }) {
  const isAuto = form.model_mode === 'auto';
  const provider = form.provider || 'openai';
  const selectedProvider = providers.find((p) => p.key === provider);
  const connectedProviders = new Set(aiConnections.map((c) => c.provider));
  const selectedAvailable = connectedProviders.has(provider) || !!(selectedProvider && selectedProvider.configured);
  const showApiKeyControl = shouldShowApiKeyControl(form, aiConnections, agentId);
  // Which key the model list is discovered with: the picked connection, or null meaning
  // this app's shared .env key for the provider.
  const connectionId = form.provider_connection_id || null;

  return (
    <Card className="p-3 space-y-3">
      <div className="text-sm font-semibold">Provider & Model</div>
      <div className="grid grid-cols-2 gap-3">
        <ProviderOrConnectionField form={form} setField={setField} providers={providers} aiConnections={aiConnections} />
        <div>
          <label className="block text-sm mb-1">Auth mode</label>
          <select
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
            value={form.auth_mode || 'api_key'}
            onChange={(e) => setField('auth_mode', e.target.value)}
          >
            <option value="api_key">Pay-per-use (API key)</option>
            <option value="subscription">Subscription</option>
          </select>
        </div>
      </div>

      {form.auth_mode === 'subscription' && (
        <Notice>Subscription auth isn't implemented for any provider yet — turns will fail until this agent is switched back to API-key mode.</Notice>
      )}
      {!selectedAvailable && form.auth_mode !== 'subscription' && (
        <Notice>No usable key for {selectedProvider ? selectedProvider.label : provider} — turns will fail until you add an AI Model connection for it (or set a key in this app's .env).</Notice>
      )}

      {showApiKeyControl && (
        <ApiKeyControl agentId={agentId} providerLabel={selectedProvider ? selectedProvider.label : provider} />
      )}

      <div className="flex items-center gap-2 pt-1">
        <span className="text-sm font-medium">Model mode</span>
        <button
          type="button"
          onClick={() => setField('model_mode', 'fixed')}
          className={`text-xs px-2 py-1 rounded-full border ${!isAuto ? 'bg-primary-600 text-white border-primary-600' : 'border-[var(--app-border)] text-secondary-500'}`}
        >
          Fixed
        </button>
        <button
          type="button"
          onClick={() => setField('model_mode', 'auto')}
          className={`text-xs px-2 py-1 rounded-full border ${isAuto ? 'bg-primary-600 text-white border-primary-600' : 'border-[var(--app-border)] text-secondary-500'}`}
        >
          Auto
        </button>
      </div>

      {isAuto ? <AutoModelFields form={form} setField={setField} provider={provider} connectionId={connectionId} /> : (
        <ModelSelect label="Model" value={form.default_model} onChange={(v) => setField('default_model', v)} provider={provider} connectionId={connectionId} />
      )}
    </Card>
  );
}

function AutoModelFields({ form, setField, provider, connectionId }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <ModelSelect label="Light model (low intensity)" value={form.light_model} onChange={(v) => setField('light_model', v)} provider={provider} connectionId={connectionId} />
        <ModelSelect label="Heavy model (high intensity)" value={form.heavy_model} onChange={(v) => setField('heavy_model', v)} provider={provider} connectionId={connectionId} />
      </div>
      <ModelSelect label="Fallback model (used if light/heavy is incomplete)" value={form.default_model} onChange={(v) => setField('default_model', v)} provider={provider} connectionId={connectionId} />
      <div>
        <label className="block text-sm mb-1">Routing instruction</label>
        <textarea
          className="w-full min-h-[80px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm"
          value={form.model_routing_instruction || ''}
          onChange={(e) => setField('model_routing_instruction', e.target.value)}
          placeholder="e.g. HIGH only for multi-step reasoning, coding, or math word problems. LOW for simple factual questions or greetings."
        />
      </div>
      <p className="text-xs text-secondary-500">A cheap pre-flight call (on the light model) classifies each turn as LOW or HIGH before picking the real model to answer with.</p>
    </div>
  );
}

/* ---- Tab contents ---- */

const VOICE_PROVIDERS = [
  { value: '', label: 'Company default' },
  { value: 'browser', label: 'Browser (free)' },
  { value: 'google', label: 'Google Cloud TTS' },
  { value: 'elevenlabs', label: 'ElevenLabs' },
  { value: 'sarvam', label: 'Sarvam AI' },
];

const STT_PROVIDERS = [
  { value: '', label: 'Company default' },
  { value: 'browser', label: 'Browser (free)' },
  { value: 'sarvam', label: 'Sarvam AI' },
  { value: 'openai', label: 'OpenAI Whisper' },
  { value: 'elevenlabs', label: 'ElevenLabs Scribe' },
];

const VOICE_NAME_LABELS = {
  google: 'Voice name (e.g. en-US-Wavenet-D)',
  elevenlabs: 'Voice ID',
  sarvam: 'Speaker (e.g. meera, ishita)',
};

const STT_MODEL_LABELS = {
  sarvam: 'STT model (e.g. saaras:v4)',
  openai: 'STT model (e.g. gpt-4o-mini-transcribe)',
  elevenlabs: 'STT model (e.g. scribe_v1)',
};

function VoiceTab({ form, setField }) {
  const ttsProvider = form.voice_provider || '';
  const sttProvider = form.stt_provider || '';
  const showVoiceName = ttsProvider === 'google' || ttsProvider === 'elevenlabs' || ttsProvider === 'sarvam';
  const showModel = ttsProvider === 'sarvam';
  const showSttModel = sttProvider === 'sarvam' || sttProvider === 'openai' || sttProvider === 'elevenlabs';
  const voiceNameLabel = VOICE_NAME_LABELS[ttsProvider] || 'Voice name / ID';
  const sttModelLabel = STT_MODEL_LABELS[sttProvider] || 'STT model';

  return (
    <div className="space-y-4">
      <SectionCard title="Text-to-Speech" help="Override the company voice settings for this agent. Leave fields empty to inherit from the company default.">
        <Select
          label="TTS Provider"
          options={VOICE_PROVIDERS}
          value={ttsProvider}
          onChange={(v) => setField('voice_provider', v)}
          allowClear
        />
        {showVoiceName && (
          <Input
            label={voiceNameLabel}
            value={form.voice_voice}
            onChange={(e) => setField('voice_voice', e.target.value)}
            placeholder={`Leave empty to use company default`}
          />
        )}
        {showModel && (
          <Input
            label="TTS model (e.g. bulbul:v3)"
            value={form.voice_model}
            onChange={(e) => setField('voice_model', e.target.value)}
            placeholder="Leave empty to use company default"
          />
        )}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Rate (0.5–2.0)"
            type="number"
            step="0.1"
            min="0.5"
            max="2.0"
            value={form.voice_rate}
            onChange={(e) => setField('voice_rate', e.target.value)}
            placeholder="Company default"
          />
          <Input
            label="Pitch (-1 to 1)"
            type="number"
            step="0.1"
            min="-1"
            max="1"
            value={form.voice_pitch}
            onChange={(e) => setField('voice_pitch', e.target.value)}
            placeholder="Company default"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Stability (0–1)"
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={form.voice_stability}
            onChange={(e) => setField('voice_stability', e.target.value)}
            placeholder="Company default"
          />
          <Input
            label="Style (0–1)"
            type="number"
            step="0.1"
            min="0"
            max="1"
            value={form.voice_style}
            onChange={(e) => setField('voice_style', e.target.value)}
            placeholder="Company default"
          />
        </div>
      </SectionCard>

      <SectionCard title="Speech-to-Text" help="Override the company STT settings for this agent. Leave fields empty to inherit from the company default.">
        <Select
          label="STT Provider"
          options={STT_PROVIDERS}
          value={sttProvider}
          onChange={(v) => setField('stt_provider', v)}
          allowClear
        />
        {showSttModel && (
          <Input
            label={sttModelLabel}
            value={form.stt_model}
            onChange={(e) => setField('stt_model', e.target.value)}
            placeholder="Leave empty to use company default"
          />
        )}
      </SectionCard>
    </div>
  );
}

function IdentityTab({ form, setField, isNew, apps }) {
  return (
    <div className="space-y-4">
      <Card className="p-3 space-y-3">
        <div className="text-sm font-semibold">Identity</div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name" value={form.name || ''} onChange={(e) => setField('name', e.target.value)} />
          <Input label={isNew ? 'Key (slug, immutable after create)' : 'Key'} value={form.key || ''} disabled={!isNew} placeholder={isNew ? 'e.g. support_frontdesk' : undefined} onChange={isNew ? (e) => setField('key', e.target.value) : undefined} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Icon (lucide name)" value={form.icon || ''} onChange={(e) => setField('icon', e.target.value)} />
          <Input label="Color" value={form.color || ''} onChange={(e) => setField('color', e.target.value)} />
        </div>
        <Input label="Monthly budget (USD)" type="number" value={form.monthly_budget_usd ?? ''} onChange={(e) => setField('monthly_budget_usd', e.target.value)} />
        <Input label="Description" value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />
        {apps.length > 0 && (
          <Select
            label="Application (scope to one app, or leave empty for company-wide)"
            options={[{ label: 'Company-wide (all apps)', value: '' }, ...apps.map((a) => ({ label: a.name, value: String(a.id) }))]}
            value={form.application_id != null ? String(form.application_id) : ''}
            onChange={(v) => setField('application_id', v ? Number(v) : null)}
            allowClear
            searchable
          />
        )}
      </Card>
    </div>
  );
}

function LlmPromptTab({ form, setField, providers, aiConnections, agentId }) {
  return (
    <div className="space-y-4">
      <ModelSection form={form} setField={setField} providers={providers} aiConnections={aiConnections} agentId={agentId} />
      <Card className="p-3 space-y-3">
        <div className="text-sm font-semibold">System prompt template</div>
        <textarea
          className="w-full min-h-[200px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm font-mono"
          value={form.system_prompt_template || ''}
          onChange={(e) => setField('system_prompt_template', e.target.value)}
        />
      </Card>
    </div>
  );
}

/** "Procurely — https://… — 19 tools, 0 armed": the armed count is the load-bearing half,
 *  since only approved AND enabled tools are bound to the agent at runtime. */
function mcpOptionLabel(p) {
  const inactive = p.is_active ? '' : ' (inactive)';
  const drifted = p.status === 'drifted' ? ' — drifted, not bound' : '';
  const counts = p.tool_count == null ? '' : ` — ${p.tool_count} tools, ${p.armed_count || 0} armed`;
  return `${p.name} — ${p.base_url}${inactive}${drifted}${counts}`;
}

/**
 * Armed, non-destructive provider tools, as auto-approve options.
 *
 * Without these the picker only ever listed built-in catalog tools, so an armed MCP tool was
 * stuck behind a per-call Human Proxy decision forever. The server decides what lands in
 * `approvable_tools` — destructive tools never do — and re-checks at call time, so this is a
 * convenience, not the gate.
 */
function approvableProviderOptions(toolProviders) {
  return toolProviders.flatMap((p) => (p.approvable_tools || []).map((name) => ({
    value: name,
    label: `${name} — ${p.name}`,
  })));
}

function WorkflowSkillCard({ workflow, onDetach }) {
  const inputCount = parseJsonArray(workflow.input_schema_json).length;

  return (
    <div className="flex items-center justify-between p-2.5 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] hover:border-primary-500/40 transition-colors">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="p-1.5 rounded-md bg-purple-500/10 text-purple-600 dark:text-purple-400 shrink-0">
          <Workflow size={15} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-xs text-[var(--app-text)] truncate">{workflow.name}</span>
            <code className="text-[10px] px-1 py-0.5 rounded bg-secondary-500/10 font-mono text-secondary-600">
              {workflow.key}
            </code>
            <span className="text-[10px] text-secondary-400">v{workflow.version || 1}</span>
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                workflow.is_active
                  ? 'bg-emerald-500/10 text-emerald-600'
                  : 'bg-secondary-500/10 text-secondary-500'
              }`}
            >
              {workflow.is_active ? 'Active' : 'Draft'}
            </span>
          </div>
          <div className="text-[11px] text-secondary-500 mt-0.5">
            {inputCount} input parameter{inputCount === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0 ml-2">
        <Link
          to={`/app-workflow/${workflow.id}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Open in Workflow Studio canvas"
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1 rounded hover:bg-primary-50 dark:hover:bg-primary-950/40 transition-colors"
        >
          <span>Canvas</span>
          <ExternalLink size={11} />
        </Link>
        <button
          type="button"
          onClick={() => onDetach(workflow.id)}
          title="Detach workflow from this agent"
          className="p-1 rounded text-secondary-400 hover:text-red-600 hover:bg-red-500/10 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

function WorkflowsSection({ form, workflows, setField }) {
  const attachedWorkflows = workflows.filter((w) => form.workflowIds.includes(w.id));
  const availableWorkflows = workflows.filter((w) => !form.workflowIds.includes(w.id));

  const handleAttach = (id) => {
    if (!id) return;
    const next = [...form.workflowIds, Number(id)];
    setField('workflowIds', next);
  };

  const handleDetach = (id) => {
    setField('workflowIds', form.workflowIds.filter((wId) => wId !== id));
  };

  return (
    <SectionCard
      title="Workflows"
      count={form.workflowIds.length}
      help="Pick the workflows this agent may run as tools. Each attached workflow is exposed to the model with its typed input contract."
    >
      {workflows.length === 0 ? (
        <p className="text-sm text-secondary-500">
          No workflows exist yet — create one under &quot;Workflows&quot; in the sidebar.
        </p>
      ) : (
        <div className="space-y-3">
          {availableWorkflows.length > 0 && (
            <Select
              label="Attach a workflow"
              searchable
              allowClear
              placeholder="Search workflows to attach as skill…"
              options={availableWorkflows.map((w) => ({
                value: w.id,
                label: `${w.name} (${w.key})${w.is_active ? '' : ' — draft'}`,
              }))}
              value={null}
              onChange={handleAttach}
            />
          )}

          {attachedWorkflows.length > 0 && (
            <div className="space-y-2">
              {attachedWorkflows.map((w) => (
                <WorkflowSkillCard key={w.id} workflow={w} onDetach={handleDetach} />
              ))}
            </div>
          )}

          {attachedWorkflows.length === 0 && availableWorkflows.length > 0 && (
            <p className="text-xs text-secondary-500">
              No workflows attached. Select a workflow above to grant this agent access to run it.
            </p>
          )}
        </div>
      )}
    </SectionCard>
  );
}

function McpServersSection({
  mcpProviders, currentMcpIds, handleMcpChange, unarmedMcp, hasDriftedMcp, onOpenCreate,
}) {
  return (
    <SectionCard
      title="MCP Servers" count={currentMcpIds.length}
      help="Remote MCP servers — like knowledge bases, attach the servers this agent may call. Tools are discovered at the endpoint (e.g. https://procurely.stage.drm3.org/api/mcp), pinned on sync, and require approval before the agent can use them. Empty means none. When many tools are armed, each turn binds the ~48 most relevant to the question (semantic selection); task-surface turns additionally drop task-forbidden tools."
    >
      {mcpProviders.length === 0 ? (
        <div className="space-y-2">
          <p className="text-sm text-secondary-500">No MCP servers yet — add your first to attach it here. Example: <code>https://procurely.stage.drm3.org/api/mcp</code></p>
          <Button size="sm" variant="secondary" onClick={onOpenCreate}>Add MCP server</Button>
        </div>
      ) : (
        <>
          <Select
            label="MCP servers this agent may use (empty = none)"
            multiSelect searchable allowClear maxTagCount={6}
            placeholder="Search MCP servers…"
            options={mcpProviders.map((p) => ({ value: p.id, label: mcpOptionLabel(p) }))}
            value={currentMcpIds}
            onChange={handleMcpChange}
          />
          {unarmedMcp.length > 0 && (
            <Alert variant="warning" title="Attached, but none of its tools are armed">
              {unarmedMcp.map((p) => p.name).join(', ')} — a discovered tool is bound only
              once a human has approved AND enabled it, so this agent currently gets no
              tools from it. Arm them on the Tool Provider detail page (&quot;Approve
              &amp; enable all&quot;).
            </Alert>
          )}
          {hasDriftedMcp && (
            <Alert variant="warning" title="Drifted — not bound">
              One of the attached MCP servers has drifted (its remote tool list no longer matches what was approved). None of its tools will be bound until a human re-approves it on the Tool Provider detail page.
            </Alert>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={onOpenCreate}>Add MCP server</Button>
            {currentMcpIds.length > 0 && mcpProviders.some((p) => currentMcpIds.includes(p.id)) && (
              <span className="text-xs text-secondary-500 self-center">Manage approvals & sync on the Tool Provider detail page</span>
            )}
          </div>
        </>
      )}
    </SectionCard>
  );
}

function OtherProvidersSection({ otherProviders, toolProvidersCount, currentOtherIds, handleOtherChange }) {
  return (
    <SectionCard
      title="Other Tool Providers" count={currentOtherIds.length}
      help="Customer REST APIs and browser-page tools. Empty means none."
    >
      {otherProviders.length === 0 ? (
        <p className="text-sm text-secondary-500">
          {toolProvidersCount === 0
            ? 'No tool providers exist yet — create one under "Tool Providers" in the sidebar.'
            : 'No HTTP / browser providers — only MCP servers exist. Create other kinds from the Tool Providers page.'}
        </p>
      ) : (
        <Select
          label="Other providers this agent may use (empty = none)"
          multiSelect searchable allowClear maxTagCount={6}
          placeholder="Search providers…"
          options={otherProviders.map((p) => ({ value: p.id, label: `${p.name} — ${p.kind}${p.is_active ? '' : ' (inactive)'}${p.status === 'drifted' ? ' — drifted, not bound' : ''}` }))}
          value={currentOtherIds}
          onChange={handleOtherChange}
        />
      )}
    </SectionCard>
  );
}

/**
 * Mock APIs get their own card rather than sitting in "Other Tool Providers": a mock is
 * a stand-in the team authored, not a third party's API, and the distinction decides
 * whether the agent is talking to something real — which is the single most important
 * thing to be able to see at a glance while building.
 */
function MockApisSection({ mockProviders, currentMockIds, handleMockChange }) {
  return (
    <SectionCard
      title="Mock APIs" count={currentMockIds.length}
      help="Mock environments whose routes this agent may call as tools. Empty means none — a mock is always an explicit binding."
    >
      {mockProviders.length === 0 ? (
        <p className="text-sm text-secondary-500">
          No mock APIs with routes yet — create one under &quot;Mock API&quot; in the sidebar and save a spec.
        </p>
      ) : (
        <>
          <Select
            label="Mock APIs this agent may call (empty = none)"
            multiSelect searchable allowClear maxTagCount={6}
            placeholder="Search mock APIs…"
            options={mockProviders.map((p) => ({
              value: p.id,
              label: `${p.name}${p.is_active ? '' : ' (inactive)'}`,
            }))}
            value={currentMockIds}
            onChange={handleMockChange}
          />
          <p className="text-xs text-secondary-500 mt-1">
            While a mock environment is in <b>mock</b> mode its tools run unattended — nothing real
            happens. Switch it to proxy or record and every one of its tools becomes gated, because
            the call then reaches a real upstream.
          </p>
        </>
      )}
    </SectionCard>
  );
}

function CapabilitiesTab({ form, catalog, dynamicTools, toolProviders, workflows, setField, onToolProvidersReload }) {
  const gated = [...catalog.standard, ...catalog.privileged].filter((t) => t.category === 'gated');
  const { fetchApi } = useAppApi();
  const [showMcpCreate, setShowMcpCreate] = useState(false);

  // Partition tool providers like KBs — MCP servers get their own first-class card
  const mcpProviders = toolProviders.filter((p) => p.kind === 'mcp');
  const mockProviders = toolProviders.filter((p) => p.kind === 'mock');
  const otherProviders = toolProviders.filter((p) => p.kind !== 'mcp' && p.kind !== 'mock');
  const mcpIdSet = new Set(mcpProviders.map((p) => Number(p.id)));
  const mockIdSet = new Set(mockProviders.map((p) => Number(p.id)));
  const currentMcpIds = form.toolProviderIds.filter((id) => mcpIdSet.has(Number(id)));
  const currentMockIds = form.toolProviderIds.filter((id) => mockIdSet.has(Number(id)));
  const currentOtherIds = form.toolProviderIds.filter((id) => !mcpIdSet.has(Number(id)) && !mockIdSet.has(Number(id)));
  const selectedMcpProviders = mcpProviders.filter((p) => currentMcpIds.includes(p.id) || currentMcpIds.includes(Number(p.id)));
  const hasDriftedMcp = selectedMcpProviders.some((p) => p.status === 'drifted');
  // Attached but contributing nothing: every one of its tools is still awaiting approval,
  // so the agent will answer "I have no MCP tools" however many were discovered.
  const unarmedMcp = selectedMcpProviders.filter((p) => p.status !== 'drifted' && !p.armed_count);

  // Each handler re-merges the OTHER partitions: they are three views of one flat
  // toolProviderIds array, so replacing a slice must never drop the rest.
  const handleMcpChange = (v) => {
    setField('toolProviderIds', [...(Array.isArray(v) ? v : []), ...currentMockIds, ...currentOtherIds]);
  };
  const handleMockChange = (v) => {
    setField('toolProviderIds', [...currentMcpIds, ...(Array.isArray(v) ? v : []), ...currentOtherIds]);
  };
  const handleOtherChange = (v) => {
    setField('toolProviderIds', [...currentMcpIds, ...currentMockIds, ...(Array.isArray(v) ? v : [])]);
  };
  const handleMcpCreated = async ({ id }) => {
    // Attach before the reload: the provider already exists server-side, so a failed
    // list refresh must not cost the user the binding they just asked for.
    if (id != null) setField('toolProviderIds', [...form.toolProviderIds, id]);
    try {
      const res = await fetchApi('toolProvidersList');
      if (typeof onToolProvidersReload === 'function') onToolProvidersReload(Array.isArray(res) ? res : []);
    } catch {
      // reload failure is non-fatal — the create succeeded and Save still persists the id
    }
  };

  return (
    <div className="space-y-4">
      <SectionCard
        title="Actions (auto-approved)" count={form.autoApproveTools.length}
        help="Selected gated actions run for this agent without a per-call human approval — grant sparingly; destructive actions always keep the human gate. Armed, non-destructive tools from your MCP servers appear here too."
      >
        <Select
          label="Gated actions pre-approved for this agent"
          multiSelect searchable allowClear maxTagCount={6}
          placeholder="Search gated actions…"
          options={[...gated.map((t) => ({ value: t.name, label: t.name })), ...approvableProviderOptions(toolProviders)]}
          value={form.autoApproveTools}
          onChange={(v) => setField('autoApproveTools', Array.isArray(v) ? v : [])}
        />
      </SectionCard>

      <SectionCard
        title="Tools" count={form.allowedTools.length}
        help={`Always available to every agent: ${catalog.standard.map((t) => t.name).join(', ')}`}
      >
        <Select
          label="Privileged tools this agent may use"
          multiSelect searchable allowClear maxTagCount={6}
          placeholder="Search privileged tools (incl. start_ai_call / schedule_ai_call)…"
          options={catalog.privileged.map((t) => ({ value: t.name, label: t.name }))}
          value={form.allowedTools}
          onChange={(v) => setField('allowedTools', Array.isArray(v) ? v : [])}
        />
      </SectionCard>

      <SectionCard
        title="Connectors" count={form.connectors.length ? form.connectors.length : 'all'}
        help="Built-in tools are grouped into connectors. Leave empty to keep every connector on (default); select connectors to restrict this agent to only those packages' tools. Turning a connector off removes its tools entirely — availability, separate from the approval gates above."
      >
        <Select
          label="Connector packages this agent may use (empty = all on)"
          multiSelect searchable allowClear maxTagCount={6}
          placeholder="Search connectors…"
          options={(catalog.connectors || []).map((c) => ({ value: c.key, label: c.label }))}
          value={form.connectors}
          onChange={(v) => setField('connectors', Array.isArray(v) ? v : [])}
        />
      </SectionCard>

      <SectionCard title="Dynamic Tools" count={form.dynamicToolIds.length || 'all'} help="Leave empty to give this agent every enabled dynamic tool; pick specific tools to narrow it.">
        {dynamicTools.length === 0
          ? <p className="text-sm text-secondary-500">No dynamic tools exist yet — create one under "Dynamic Tools" in the sidebar.</p>
          : (
            <Select
              label="Dynamic tools this agent may use (empty = all enabled)"
              multiSelect searchable allowClear maxTagCount={6}
              placeholder="Search dynamic tools…"
              options={dynamicTools.map((t) => ({ value: t.id, label: `${t.name}${t.enabled ? '' : ' (disabled)'}` }))}
              value={form.dynamicToolIds}
              onChange={(v) => setField('dynamicToolIds', Array.isArray(v) ? v : [])}
            />
          )}
      </SectionCard>

      <McpServersSection
        mcpProviders={mcpProviders}
        currentMcpIds={currentMcpIds}
        handleMcpChange={handleMcpChange}
        unarmedMcp={unarmedMcp}
        hasDriftedMcp={hasDriftedMcp}
        onOpenCreate={() => setShowMcpCreate(true)}
      />

      <MockApisSection
        mockProviders={mockProviders}
        currentMockIds={currentMockIds}
        handleMockChange={handleMockChange}
      />

      <OtherProvidersSection
        otherProviders={otherProviders}
        toolProvidersCount={toolProviders.length}
        currentOtherIds={currentOtherIds}
        handleOtherChange={handleOtherChange}
      />

      <WorkflowsSection form={form} workflows={workflows} setField={setField} />

      <ToolProviderCreateDialog
        isOpen={showMcpCreate}
        onClose={() => setShowMcpCreate(false)}
        onCreated={handleMcpCreated}
        kindPreset="mcp"
        defaultBaseUrl=""
      />
    </div>
  );
}

function AttachmentsTab({ form, knowledgeBases, connections, webhookEndpoints, setField }) {
  return (
    <div className="space-y-4">
      <SectionCard title="Knowledge Bases" count={form.knowledgeBaseIds.length}>
        {knowledgeBases.length === 0
          ? <p className="text-sm text-secondary-500">No knowledge bases exist yet — create one under "Knowledge Bases" in the sidebar.</p>
          : (
            <Select
              label="Knowledge bases this agent may search"
              multiSelect searchable allowClear maxTagCount={6}
              placeholder="Search knowledge bases…"
              options={knowledgeBases.map((kb) => ({ value: kb.id, label: kb.name }))}
              value={form.knowledgeBaseIds}
              onChange={(v) => setField('knowledgeBaseIds', Array.isArray(v) ? v : [])}
            />
          )}
        {form.knowledgeBaseIds.length > 0 && (
          <Select
            label="Retrieve before answering"
            options={[
              { value: 'auto', label: 'Auto — unattended surfaces only (recommended)' },
              { value: 'always', label: 'Always — every message, including chat here' },
              { value: 'off', label: 'Off — only when the agent calls kb_search' },
            ]}
            value={form.kb_prefetch}
            onChange={(v) => setField('kb_prefetch', v)}
          />
        )}
      </SectionCard>

      <SectionCard title="Memory" count={form.memory_enabled ? 'on' : 'off'} help="When on, the agent can remember durable facts about a user across sessions and recall them later. Signed-in users and identified embed visitors each get their own memory; anonymous visitors are session-local.">
        <Select
          label="Long-term memory"
          options={[
            { value: 'off', label: 'Off — the agent remembers nothing between sessions' },
            { value: 'on', label: 'On — the agent may store and recall facts about users' },
          ]}
          value={form.memory_enabled ? 'on' : 'off'}
          onChange={(v) => setField('memory_enabled', v === 'on')}
        />
      </SectionCard>

      <SectionCard title="Privacy" count={form.pii_redaction ? 'on' : 'off'} help="When on, card numbers (Luhn-checked), emails, phone-like numbers and ID formats in user messages are masked before the text reaches any model — chat, voice transcript correction and retrieval alike. Stored messages keep the original so operators can still audit them.">
        <Select
          label="PII redaction"
          options={[
            { value: 'off', label: 'Off — user text goes to the model exactly as typed' },
            { value: 'on', label: 'On — mask PII in user text before any model sees it' },
          ]}
          value={form.pii_redaction ? 'on' : 'off'}
          onChange={(v) => setField('pii_redaction', v === 'on')}
        />
      </SectionCard>

      <SectionCard title="Connections" count={form.connectionIds.length} help="One connection per category — tools that declare a category (dynamic tools, AI voice calls) receive the matching connection's decrypted credentials at run time.">
        {connections.length === 0
          ? <p className="text-sm text-secondary-500">No connections exist yet — create one under "Connections" in the sidebar.</p>
          : (
            <Select
              label="Connections attached to this agent"
              multiSelect searchable grouped allowClear maxTagCount={6}
              placeholder="Search connections…"
              options={groupConnectionOptions(connections)}
              value={form.connectionIds}
              onChange={(v) => setField('connectionIds', enforceOnePerCategory(Array.isArray(v) ? v : [], form.connectionIds, connections))}
            />
          )}
      </SectionCard>

      <SectionCard title="Webhooks" count={form.webhookEndpointIds.length || 'all'} help="Leave empty to send this agent's call events to every subscribed webhook endpoint; pick endpoints to route them narrowly.">
        {webhookEndpoints.length === 0
          ? <p className="text-sm text-secondary-500">No webhook endpoints exist yet — register one under "AI Voice Calls → Webhooks" in the sidebar.</p>
          : (
            <Select
              label="Webhook endpoints for this agent's call events (empty = all subscribed)"
              multiSelect searchable allowClear maxTagCount={6}
              placeholder="Search webhook endpoints…"
              options={webhookEndpoints.map((w) => ({ value: w.id, label: `${w.name}${w.is_active ? '' : ' (inactive)'}` }))}
              value={form.webhookEndpointIds}
              onChange={(v) => setField('webhookEndpointIds', Array.isArray(v) ? v : [])}
            />
          )}
      </SectionCard>
    </div>
  );
}

/* ---- Main AgentDetail ---- */

function agentPayload(form) {
  return {
    name: form.name, description: form.description, icon: form.icon, color: form.color,
    default_model: form.default_model, monthly_budget_usd: form.monthly_budget_usd || null,
    system_prompt_template: form.system_prompt_template,
    allowed_tools: JSON.stringify(form.allowedTools),
    knowledge_base_ids: JSON.stringify(form.knowledgeBaseIds),
    kb_prefetch: form.kb_prefetch || 'auto',
    memory_enabled: !!form.memory_enabled,
    pii_redaction: !!form.pii_redaction,
    connection_ids: JSON.stringify(form.connectionIds),
    dynamic_tool_ids: form.dynamicToolIds.length ? JSON.stringify(form.dynamicToolIds) : null,
    workflow_ids: form.workflowIds.length ? JSON.stringify(form.workflowIds) : null,
    tool_provider_ids: form.toolProviderIds.length ? JSON.stringify(form.toolProviderIds) : null,
    webhook_endpoint_ids: form.webhookEndpointIds.length ? JSON.stringify(form.webhookEndpointIds) : null,
    auto_approve_tools: form.autoApproveTools.length ? JSON.stringify(form.autoApproveTools) : null,
    connector_ids: form.connectors.length ? JSON.stringify(form.connectors) : null,
    provider: form.provider, provider_connection_id: form.provider_connection_id || null,
    auth_mode: form.auth_mode, model_mode: form.model_mode,
    light_model: form.light_model || null, heavy_model: form.heavy_model || null,
    model_routing_instruction: form.model_routing_instruction || null,
    application_id: form.application_id || null,
    voice_provider: form.voice_provider || null,
    voice_voice: form.voice_voice || null,
    voice_model: form.voice_model || null,
    voice_rate: form.voice_rate !== '' && form.voice_rate != null ? Number(form.voice_rate) : null,
    voice_pitch: form.voice_pitch !== '' && form.voice_pitch != null ? Number(form.voice_pitch) : null,
    voice_stability: form.voice_stability !== '' && form.voice_stability != null ? Number(form.voice_stability) : null,
    voice_style: form.voice_style !== '' && form.voice_style != null ? Number(form.voice_style) : null,
    stt_provider: form.stt_provider || null,
    stt_model: form.stt_model || null,
    a2a_enabled: !!form.a2a_enabled,
    a2a_skills_json: skillsToJson(form.a2aSkills || []),
  };
}

function hydrateAgentRow(agent) {
  return {
    ...agent,
    allowedTools: parseJsonArray(agent.allowed_tools),
    knowledgeBaseIds: parseJsonArray(agent.knowledge_base_ids),
    connectionIds: parseJsonArray(agent.connection_ids),
    dynamicToolIds: parseJsonArray(agent.dynamic_tool_ids),
    workflowIds: parseJsonArray(agent.workflow_ids),
    toolProviderIds: parseJsonArray(agent.tool_provider_ids),
    webhookEndpointIds: parseJsonArray(agent.webhook_endpoint_ids),
    autoApproveTools: parseJsonArray(agent.auto_approve_tools),
    connectors: parseJsonArray(agent.connector_ids),
    kb_prefetch: agent.kb_prefetch || 'auto',
    memory_enabled: !!agent.memory_enabled,
    pii_redaction: !!agent.pii_redaction,
    voice_provider: agent.voice_provider || '',
    voice_voice: agent.voice_voice || '',
    voice_model: agent.voice_model || '',
    voice_rate: agent.voice_rate != null ? String(agent.voice_rate) : '',
    voice_pitch: agent.voice_pitch != null ? String(agent.voice_pitch) : '',
    voice_stability: agent.voice_stability != null ? String(agent.voice_stability) : '',
    voice_style: agent.voice_style != null ? String(agent.voice_style) : '',
    stt_provider: agent.stt_provider || '',
    stt_model: agent.stt_model || '',
    a2a_enabled: !!agent.a2a_enabled,
    a2aSkills: skillsFromJson(agent.a2a_skills_json),
  };
}

const asList = (res) => (Array.isArray(res) ? res : res?.data) || [];

const BLANK_AGENT = {
  key: '', name: '', description: '', icon: '', color: '',
  provider: 'openai', auth_mode: 'api_key', default_model: 'gpt-4o-mini', model_mode: 'fixed',
  light_model: '', heavy_model: '', model_routing_instruction: '',
  system_prompt_template: '', monthly_budget_usd: '',
  provider_connection_id: null, is_paused: false, application_id: null,
  kb_prefetch: 'auto', memory_enabled: false, pii_redaction: false,
  allowedTools: [], knowledgeBaseIds: [], connectionIds: [],
  dynamicToolIds: [], workflowIds: [], toolProviderIds: [], webhookEndpointIds: [], autoApproveTools: [],
  connectors: [],
  voice_provider: '', voice_voice: '', voice_model: '',
  voice_rate: '', voice_pitch: '', voice_stability: '', voice_style: '',
  stt_provider: '', stt_model: '',
  a2a_enabled: false, a2aSkills: [],
};

export default function AgentDetail() {
  const { id } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const { fetchApi } = useAppApi();
  const { openDockWithAgent } = useChatDock();

  const [original, setOriginal] = useState(isNew ? { ...BLANK_AGENT } : null);
  const [form, setForm] = useState(isNew ? { ...BLANK_AGENT } : null);
  const [catalog, setCatalog] = useState({ standard: [], privileged: [] });
  const [providers, setProviders] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [connections, setConnections] = useState([]);
  const [dynamicTools, setDynamicTools] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [toolProviders, setToolProviders] = useState([]);
  const [webhookEndpoints, setWebhookEndpoints] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      isNew ? Promise.resolve(null) : fetchApi('crud', { model: 'AppAiAgent', action: 'show', where: { id } }),
      fetchApi('agentToolCatalog'),
      fetchApi('agentProviders'),
      fetchApi('crud', { model: 'AppKnowledgeBase', action: 'list', limit: 100 }).catch(() => ({ data: [] })),
      fetchApi('crud', { model: 'AppConnection', action: 'list', limit: 200 }).catch(() => ({ data: [] })),
      fetchApi('crud', { model: 'AppDynamicTool', action: 'list', limit: 200 }).catch(() => ({ data: [] })),
      fetchApi('crud', { model: 'AppWorkflow', action: 'list', limit: 200 }).catch(() => ({ data: [] })),
      fetchApi('toolProvidersList').catch(() => []),
      fetchApi('voiceWebhookList').catch(() => []),
      fetchApi('applicationsList').catch(() => []),
    ])
      .then(([agentRes, catalogRes, providersRes, knowledgeBasesRes, connectionsRes, dynamicToolsRes, workflowsRes, toolProvidersRes, webhooksRes, appsRes]) => {
        if (!isNew) {
          const agent = agentRes && agentRes.data ? agentRes.data : agentRes;
          const hydrated = hydrateAgentRow(agent);
          setOriginal(hydrated);
          setForm(hydrated);
        }
        setCatalog(catalogRes);
        setProviders(providersRes.providers || []);
        setKnowledgeBases(asList(knowledgeBasesRes));
        setConnections(asList(connectionsRes));
        setDynamicTools(asList(dynamicToolsRes));
        setWorkflows(asList(workflowsRes));
        setToolProviders(Array.isArray(toolProvidersRes) ? toolProvidersRes : []);
        setWebhookEndpoints(Array.isArray(webhooksRes) ? webhooksRes : []);
        setApps(Array.isArray(appsRes) ? appsRes : []);
      })
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, [id]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      const data = agentPayload(form);
      if (isNew) {
        const created = await fetchApi('crud', {
          model: 'AppAiAgent', action: 'create', data: { ...data, key: form.key },
        });
        const newId = created?.data?.id ?? created?.[0]?.id ?? created?.id;
        if (!newId) throw new Error('Created the agent, but could not read its new id back from the response.');
        nav(`/app-ai-agent/${newId}`, { replace: true });
        return;
      }
      await fetchApi('crud', { model: 'AppAiAgent', action: 'update', where: { id }, data });
      setOriginal(form);
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePaused = async () => {
    const next = !form.is_paused;
    const patch = { is_paused: next, paused_reason: next ? 'Paused from admin UI' : null };
    await fetchApi('crud', { model: 'AppAiAgent', action: 'update', where: { id }, data: patch });
    setForm((f) => ({ ...f, ...patch }));
    setOriginal((o) => ({ ...o, ...patch }));
  };

  const remove = async () => {
    if (!window.confirm(`Delete agent "${form.name}"? This cannot be undone.`)) return;
    try {
      await fetchApi('crud', { model: 'AppAiAgent', action: 'delete', where: { id } });
      nav('/app-ai-agent');
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };

  if (loading) return <PageShell><Loader text="Loading agent…" /></PageShell>;
  if (err && !form) return <PageShell><Alert variant="error" title="Could not load">{err}</Alert></PageShell>;
  if (!form) return null;

  const dirty = fieldsChanged(form, original);
  const aiConnections = connections.filter((c) => c.category_key === 'ai-model' && c.is_active !== false && c.is_active !== 0);

  return (
    <PageShell width="narrow" className="space-y-3">
      <PageHeader
        backTo="/app-ai-agent"
        backLabel="All agents"
        title={isNew ? 'New Agent' : form.name}
        meta={(
          <>
            {!isNew && (
              <Badge variant={form.is_paused ? 'warning' : 'success'} size="sm">
                {form.is_paused ? 'paused' : 'active'}
              </Badge>
            )}
          </>
        )}
        actions={(
          <>
            {!isNew && (
              <Button size="sm" variant="secondary" onClick={() => openDockWithAgent(form.key)}>
                <MessageCircle size={14} className="mr-1 inline" /> Chat
              </Button>
            )}
            <Can module="agents" action="govern">
              {!isNew && <Button size="sm" variant="secondary" onClick={togglePaused}>{form.is_paused ? 'Resume' : 'Pause'}</Button>}
              {!isNew && (
                <Button size="sm" variant="secondary" onClick={remove}>
                  <Trash2 size={14} className="mr-1 inline" /> Delete
                </Button>
              )}
              <Button size="sm" onClick={save} disabled={saving || (isNew ? !form.key || !form.name : !dirty)}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Can>
          </>
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}

      <Tabs defaultIndex={0} variant="line" size="sm">
        <Tabs.List>
          <Tabs.Tab index={0}>Identity</Tabs.Tab>
          <Tabs.Tab index={1}>LLM & System Prompt</Tabs.Tab>
          <Tabs.Tab index={2}>Capabilities</Tabs.Tab>
          <Tabs.Tab index={3}>Attachments</Tabs.Tab>
          <Tabs.Tab index={4}>Voice</Tabs.Tab>
          <Tabs.Tab index={5}>A2A</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panels className="pt-3">
          <Tabs.Panel index={0}>
            <IdentityTab form={form} setField={setField} isNew={isNew} apps={apps} />
          </Tabs.Panel>
          <Tabs.Panel index={1}>
            <LlmPromptTab form={form} setField={setField} providers={providers} aiConnections={aiConnections} agentId={isNew ? null : id} />
          </Tabs.Panel>
          <Tabs.Panel index={2}>
            <CapabilitiesTab form={form} catalog={catalog} dynamicTools={dynamicTools} toolProviders={toolProviders} workflows={workflows} setField={setField} onToolProvidersReload={(list) => setToolProviders(list)} />
          </Tabs.Panel>
          <Tabs.Panel index={3}>
            <AttachmentsTab form={form} knowledgeBases={knowledgeBases} connections={connections} webhookEndpoints={webhookEndpoints} setField={setField} />
          </Tabs.Panel>
          <Tabs.Panel index={4}>
            <VoiceTab form={form} setField={setField} />
          </Tabs.Panel>
          <Tabs.Panel index={5}>
            <AgentA2aTab form={form} setField={setField} isNew={isNew} />
          </Tabs.Panel>
        </Tabs.Panels>
      </Tabs>
    </PageShell>
  );
}
